import dayjs, { ConfigType } from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import utc from 'dayjs/plugin/utc';
import { LocaleDateVariant } from '../types';

dayjs.extend(utc);
dayjs.extend(advancedFormat);

/**
 * Helper that formats a date according to predefined variants.
 */
export const formatLocaleDate = (
    date: number,
    locale?: Intl.LocalesArgument | undefined,
    variant?: LocaleDateVariant
) => {
    switch (variant) {
        case LocaleDateVariant.day:
            return new Date(date)
                .toLocaleDateString(locale, {
                    timeZone: 'UTC',
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                })
                .replace(/,/g, '');
        case LocaleDateVariant.quarterly:
            return dayjs.utc(date).format('[Q]Q YYYY');
        case LocaleDateVariant.year:
            return dayjs.utc(date).format('YYYY');
        // Defaults to shortMonthYear
        case LocaleDateVariant.shortMonthYear:
        default:
            return new Date(date).toLocaleDateString(locale, { timeZone: 'UTC', year: 'numeric', month: 'short' });
    }
};

// Function to extract MMM DD YYYY from date object
export const getFormattedDate = (date: Date) =>
    date
        .toLocaleString('en-us', {
            month: 'short',
            day: 'numeric',
            timeZone: 'UTC'
        })
        .replace(/,/g, '');

/**
 * Determines if times are equivalent within a minute of granularity.
 * @example
 * isSameTime(1667333000000, '2022-11-01T20:03:20Z');
 * // -> true
 */
export const isSameTime = (time1: ConfigType, time2: ConfigType) => {
    return dayjs.utc(time1).isSame(dayjs.utc(time2), 'minute');
};

/**
 * Determines whether two time values are in the same timespan bucket.
 */
export const inSameTimespan = (time1: ConfigType, time2: ConfigType, variant: LocaleDateVariant) => {
    const date1 = dayjs.utc(time1);
    const date2 = dayjs.utc(time2);

    switch (variant) {
        case LocaleDateVariant.shortMonthYear:
            return date1.isSame(date2, 'month');
        case LocaleDateVariant.quarterly: {
            const quarter1 = Math.floor(date1.month() / 3);
            const quarter2 = Math.floor(date2.month() / 3);
            return date1.year() === date2.year() && quarter1 === quarter2;
        }
        case LocaleDateVariant.year:
            return date1.isSame(date2, 'year');
        default:
            return false;
    }
};

/**
 * Determines the start of the month for any given date.
 */
export const getMonthStart = (inputDate: Date) => {
    const year = inputDate.getUTCFullYear();
    const month = inputDate.getUTCMonth();
    const result = new Date(year, month, 1);
    result.setUTCHours(0, 0, 0, 0);
    return result;
};

/**
 * Determines the end of the month for any given date.
 */
export const getMonthEnd = (inputDate: Date) => {
    const result = new Date(inputDate.getTime());
    const endYear = inputDate.getUTCFullYear();
    const endMonth = inputDate.getUTCMonth();
    // set to first day of the next month; defaults to midnight
    result.setUTCFullYear(endYear, endMonth + 1, 1);
    // set back one hour, to get to last day of current month
    result.setUTCHours(0, 0, 0, -1);
    result.setUTCHours(23, 59, 59, 999);
    return result;
};

/**
 *  Determines the localized short month and day of a given date
 */

export const getMonthDay = (inputDate: Date) => {
    const date = new Date(inputDate);
    const month = date.toLocaleString('default', { month: 'short', timeZone: 'UTC' });
    const day = date.getUTCDate();
    return `${month} ${day}`;
};

/**
 * Determines the end of the quarter for any given date.
 */
export const getQuarter = (inputDate: Date) => {
    const month = inputDate.getUTCMonth();
    const quarter = Math.floor(month / 3);
    const start = quarter * 3;
    return {
        start,
        end: start + 2
    };
};

/**
 * Get the datetime of the end of the quarter for any given date.
 */
export const getQuarterEnd = (inputDate: Date) => {
    // create a new date object from the inputDate
    const result = new Date(inputDate.getTime());
    // get the month that ends the quarter inputDate is in
    const { end: quarterEndMonth } = getQuarter(inputDate);
    //
    // set the day to 1
    //
    // note:
    // If we don't do this, passing May 31 to this function (for example)
    // would give an incorrect result.
    // inputDate = May 31, 2023
    // quarterEndMonth = 5 (June).
    // Set the month to June = June 31 = July 1
    // result = July 31, 2023
    //
    result.setUTCDate(1);
    result.setUTCMonth(quarterEndMonth);
    return getMonthEnd(result);
};

/**
 * Get the datetime of the start of the quarter for any given date.
 */
export const getQuarterStart = (inputDate: Date) => {
    // create a new date object from the inputDate
    const result = new Date(inputDate.getTime());
    // get the month that starts the quarter inputDate is in
    const { start: quarterStartMonth } = getQuarter(inputDate);
    // Set the day to 1, so we don't run into non-existant dates (ex June 30)
    // when we change the month on the following line.
    // See further explaination in `getQuarterEnd`
    result.setUTCDate(1);
    result.setUTCMonth(quarterStartMonth);
    return getMonthStart(result);
};

/**
 * Returns a date object corresponding to the last day of the year that the input date belongs to
 */
export const getYearEnd = (inputDate: Date) => {
    const year = inputDate.getUTCFullYear();
    return new Date(Date.UTC(year, 11, 31, 23, 59, 59, 999));
};
